import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import styled from "styled-components";
import Navbar from "./Navbar";
import Footer from "./Footer";

const Container = styled.div`
  min-height: 100vh;
  background: #f9fafb;
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 120px 20px 40px;

  @media screen and (max-width: 768px) {
    padding: 100px 15px 30px;
  }
`;

const BackButton = styled.button`
  background: transparent;
  border: 1px solid #010606;
  color: #010606;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  margin-bottom: 20px;
  transition: all 0.3s ease;

  &:hover {
    background: #010606;
    color: #fff;
  }
`;

const BlogHeader = styled.div`
  margin-bottom: 30px;
`;

const BlogTitle = styled.h1`
  font-size: 2.5rem;
  color: #1f2937;
  margin-bottom: 20px;
  line-height: 1.2;

  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const MetaSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const AuthorAvatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
`;

const AuthorInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.div`
  font-weight: bold;
  font-size: 1rem;
  color: #1f2937;
`;

const PublishDate = styled.div`
  font-size: 0.9rem;
  color: #6b7280;
`;

const StatsSection = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${(props) => (props.liked ? "#fef2f2" : "#f3f4f6")};
  border: 1px solid ${(props) => (props.liked ? "#fca5a5" : "#e5e7eb")};
  border-radius: 20px;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  transition: all 0.3s ease;
  color: ${(props) => (props.liked ? "#dc2626" : "#374151")};

  svg {
    width: 18px;
    height: 18px;
    fill: ${(props) => (props.liked ? "#dc2626" : "none")};
    stroke: ${(props) => (props.liked ? "#dc2626" : "currentColor")};
  }

  &:hover {
    ${(props) =>
      props.clickable &&
      `
      background: #e5e7eb;
      transform: translateY(-2px);
    `}
  }
`;

const ImageGallery = styled.div`
  margin: 30px 0;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const MainImage = styled.img`
  width: 100%;
  height: 500px;
  object-fit: cover;

  @media screen and (max-width: 768px) {
    height: 300px;
  }
`;

const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
  padding: 15px;
  background: #f9fafb;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  border: ${(props) => (props.active ? "3px solid #010606" : "none")};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const BlogContent = styled.div`
  background: #fff;
  padding: 40px;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;

  @media screen and (max-width: 768px) {
    padding: 25px 20px;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #374151;
  white-space: pre-wrap;
`;

const CommentSection = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  @media screen and (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const CommentTitle = styled.h3`
  font-size: 1.5rem;
  color: #1f2937;
  margin-bottom: 20px;
`;

const CommentForm = styled.form`
  margin-bottom: 30px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: #010606;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: #010606;
  }
`;

const SubmitButton = styled.button`
  background: #010606;
  color: #fff;
  padding: 12px 30px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #333;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Comment = styled.div`
  padding: 15px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const CommentAvatar = styled.img`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  object-fit: cover;
`;

const CommentAuthor = styled.div`
  font-weight: bold;
  font-size: 0.95rem;
  color: #1f2937;
`;

const CommentDate = styled.div`
  font-size: 0.85rem;
  color: #6b7280;
  margin-left: auto;
`;

const CommentText = styled.p`
  color: #374151;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-left: 45px;
`;

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 1.2rem;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fca5a5;
  color: #dc2626;
  padding: 20px;
  border-radius: 8px;
  margin: 20px 0;
  text-align: center;
`;

const PublicViewBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [likes, setLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [userName, setUserName] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Fetch blog data
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${baseUrl}/auth/blog/${id}`);
        const blogData = response.data;

        setBlog(blogData);
        setLikes(blogData.likeCount || 0);
        setComments(blogData.comments || []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching blog:", error);
        setError("Failed to load blog post");
        setLoading(false);
      }
    };

    if (id) {
      fetchBlog();
    }
  }, [id, baseUrl]);

  const handleLike = async () => {
    try {
      const response = await axios.post(`${baseUrl}/auth/like`, {
        blogId: id,
      });

      if (response.data.liked) {
        setLikes(response.data.likeCount || likes + 1);
        setIsLiked(true);
      } else {
        setLikes(response.data.likeCount || likes - 1);
        setIsLiked(false);
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      alert("Failed to like post");
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await axios.post(`${baseUrl}/auth/comment`, {
        blogId: id,
        text: newComment,
        userName: userName.trim() || "Anonymous",
      });

      setComments([response.data, ...comments]);
      setNewComment("");
      setUserName("");
    } catch (error) {
      console.error("Error adding comment:", error);
      alert("Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "1 day ago";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return formatDate(dateString);
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) {
      return "https://via.placeholder.com/800x400/4B5563/FFFFFF?text=Blog+Image";
    }
    const cleanImageUrl = imageUrl.startsWith("/")
      ? imageUrl.substring(1)
      : imageUrl;
    return `${baseUrl}/${cleanImageUrl}`;
  };

  if (loading) {
    return (
      <Container>
        <Navbar alwaysBlack={true} />
        <ContentWrapper>
          <Loading>Loading blog post...</Loading>
        </ContentWrapper>
        <Footer />
      </Container>
    );
  }

  if (error || !blog) {
    return (
      <Container>
        <Navbar alwaysBlack={true} />
        <ContentWrapper>
          <ErrorMessage>
            {error || "Blog post not found"}
            <br />
            <BackButton onClick={handleBack} style={{ marginTop: "15px" }}>
              Back to Home
            </BackButton>
          </ErrorMessage>
        </ContentWrapper>
        <Footer />
      </Container>
    );
  }

  const blogImages = blog.images && blog.images.length > 0 ? blog.images : [];
  const displayImages =
    blogImages.length > 0 ? blogImages : [{ imageUrl: blog.imageUrl }];

  return (
    <Container>
      <Navbar alwaysBlack={true} />
      <ContentWrapper>
        <BackButton onClick={handleBack}>← Back to Home</BackButton>

        <BlogHeader>
          <BlogTitle>{blog.title}</BlogTitle>
          <MetaSection>
            <AuthorSection>
              <AuthorAvatar
                src="https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=1061&q=80"
                alt={blog.author}
              />
              <AuthorInfo>
                <AuthorName>{blog.author}</AuthorName>
                <PublishDate>Published on {formatDate(blog.date)}</PublishDate>
              </AuthorInfo>
            </AuthorSection>

            <StatsSection>
              <StatItem liked={isLiked} clickable onClick={handleLike}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
                {likes}
              </StatItem>
              <StatItem>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
                  />
                </svg>
                {comments.length}
              </StatItem>
            </StatsSection>
          </MetaSection>
        </BlogHeader>

        <ImageGallery>
          <MainImage
            src={getImageUrl(displayImages[selectedImageIndex].imageUrl)}
            alt={blog.title}
            onError={(e) => {
              e.target.src =
                "https://via.placeholder.com/800x400/4B5563/FFFFFF?text=Blog+Image";
            }}
          />
          {displayImages.length > 1 && (
            <ThumbnailGrid>
              {displayImages.map((img, index) => (
                <Thumbnail
                  key={index}
                  src={getImageUrl(img.imageUrl)}
                  alt={`${blog.title} - Image ${index + 1}`}
                  active={selectedImageIndex === index}
                  onClick={() => setSelectedImageIndex(index)}
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/150/4B5563/FFFFFF?text=Image";
                  }}
                />
              ))}
            </ThumbnailGrid>
          )}
        </ImageGallery>

        <BlogContent>
          <Description>{blog.description}</Description>
        </BlogContent>

        <CommentSection>
          <CommentTitle>
            Comments ({comments.length})
          </CommentTitle>

          <CommentForm onSubmit={handleCommentSubmit}>
            <Input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Your name (optional)"
              disabled={submittingComment}
            />
            <TextArea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Share your thoughts..."
              disabled={submittingComment}
              required
            />
            <SubmitButton
              type="submit"
              disabled={submittingComment || !newComment.trim()}
            >
              {submittingComment ? "Posting..." : "Post Comment"}
            </SubmitButton>
          </CommentForm>

          {comments.length > 0 ? (
            <CommentList>
              {comments.map((comment) => (
                <Comment key={comment.id}>
                  <CommentHeader>
                    <CommentAvatar
                      src={
                        comment.userAvatar ||
                        "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&auto=format&fit=crop&w=1061&q=80"
                      }
                      alt={comment.userName}
                    />
                    <CommentAuthor>{comment.userName}</CommentAuthor>
                    <CommentDate>
                      {formatCommentDate(comment.createdAt)}
                    </CommentDate>
                  </CommentHeader>
                  <CommentText>{comment.text}</CommentText>
                </Comment>
              ))}
            </CommentList>
          ) : (
            <p style={{ textAlign: "center", color: "#6b7280" }}>
              No comments yet. Be the first to comment!
            </p>
          )}
        </CommentSection>
      </ContentWrapper>
      <Footer />
    </Container>
  );
};

export default PublicViewBlog;
